import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { type ChangeEventType, type ChangeHistoryRow } from '../../models/change-history.model';

export interface ChangeHistoryDialogData {
  /** Translation key for the dialog heading, e.g. `REFERRAL.HISTORY_TITLE`. */
  titleKey: string;
  /** Free text identifying the record — a referral's business type, a member pair. Not translated. */
  subtitle?: string;
  rows: ChangeHistoryRow[];
  /** Translation key prefix for status values, e.g. `REFERRAL.STATUS_`. Omit where the timeline has no statuses. */
  statusPrefix?: string;
}

/**
 * Read-only change-history timeline (TDD 14 §7.5), shared by referrals, R-R
 * meetings, TYFCB and testimonials.
 *
 * <p>
 * <b>Read-only on purpose.</b> History is append-only server-side (TDD 14
 * §7.6) — a trail an officer can edit isn't one. This dialog therefore has no
 * actions beyond closing, which is also why it's a dialog rather than a route:
 * it's something you consult while looking at a record, not a place you go.
 *
 * <p>
 * Newest-first, matching the order the API returns. Deliberately not
 * paginated: the volume is a handful of rows per record, and paging a dispute
 * timeline would hide the beginning of the story behind a click.
 */
@Component({
  selector: 'app-change-history-dialog',
  standalone: false,
  templateUrl: './change-history-dialog.component.html',
  styleUrl: './change-history-dialog.component.scss'
})
export class ChangeHistoryDialogComponent {
  public dialogRef = inject<MatDialogRef<ChangeHistoryDialogComponent>>(MatDialogRef);
  public data = inject<ChangeHistoryDialogData>(MAT_DIALOG_DATA);

  /** Material icon per event type — a glance should distinguish "logged" from "closed" from "someone edited this". */
  icon(eventType: ChangeEventType): string {
    switch (eventType) {
      case 'CREATED':
        return 'add_circle_outline';
      case 'STATUS_CHANGE':
        return 'trending_flat';
      case 'EDIT':
        return 'edit';
      case 'NOTE':
        return 'sticky_note_2';
      default:
        return 'circle';
    }
  }

  /**
   * Row-level prefix wins over the dialog-level one, so a timeline that mixes
   * status vocabularies (a member's dues rows alongside their standing rows)
   * resolves each to its own key set rather than rendering a raw enum name.
   */
  statusKey(row: ChangeHistoryRow, status: string | null | undefined): string {
    if (!status) return '';
    return `${row.statusPrefix ?? this.data.statusPrefix ?? ''}${status}`;
  }

  close(): void {
    this.dialogRef.close();
  }
}
